/**
 * Media Carousel — parallax and accessibility enhancements.
 *
 * Structure:
 *  1. Constants & utilities
 *  2. Parallax (MediaCarouselParallax)
 *  3. Carousel accessibility & pagination
 *  4. Section initialisation
 */

/* -------------------------------------------------------------------------- */
/* 1. Constants & utilities                                                   */
/* -------------------------------------------------------------------------- */

const DESKTOP_MIN = 1025;
const PARALLAX_CLIP_OVERSCAN = 2;

const SELECTORS = {
  section: ".media-carousel",
  carousel: "s-carousel",
  parallaxShell: "[js-media-carousel-parallax-shell]",
  parallaxTarget: "[js-media-carousel-parallax]",
  status: "[data-media-carousel-status]",
  pagination: "[data-media-carousel-pagination]",
  paginationBullet: "[data-slide-index]",
};

const CLASSES = {
  parallax: "media-carousel--parallax",
  parallaxMounted: "media-carousel--parallax-mounted",
  parallaxReady: "media-carousel--parallax-ready",
  parallaxActive: "media-carousel__parallax-target--active",
  parallaxConcealed: "media-carousel__parallax-target--concealed",
};

const PAGINATION_KEYS = new Set([
  "ArrowRight",
  "ArrowDown",
  "ArrowLeft",
  "ArrowUp",
  "Home",
  "End",
]);

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function getViewportHeight() {
  return window.visualViewport?.height ?? window.innerHeight;
}

function getViewportMetrics() {
  const visualViewport = window.visualViewport;

  return {
    height: visualViewport?.height ?? window.innerHeight,
    width: visualViewport?.width ?? window.innerWidth,
    offsetTop: visualViewport?.offsetTop ?? 0,
    offsetLeft: visualViewport?.offsetLeft ?? 0,
  };
}

function getParallaxHorizontalBounds(section, media, swiper) {
  const metrics = getViewportMetrics();

  if (isMultiSlideViewport(section, swiper)) {
    const mediaRect = media.getBoundingClientRect();

    return {
      left: Math.round(mediaRect.left - metrics.offsetLeft),
      width: Math.round(mediaRect.width),
    };
  }

  return {
    left: -metrics.offsetLeft,
    width: Math.round(metrics.width),
  };
}

function getSwiperIndex(swiper) {
  return typeof swiper.realIndex === "number"
    ? swiper.realIndex
    : swiper.activeIndex;
}

function getSlideCount(section, swiper) {
  return parseInt(section.dataset.slideCount, 10) || swiper?.slides?.length || 0;
}

function parseSlideIndex(element) {
  return parseInt(element.getAttribute("data-slide-index"), 10);
}

function bindCarouselReady(section, onReady) {
  const carousel = section.querySelector(SELECTORS.carousel);
  if (!carousel) return;

  const attach = (swiper) => {
    if (swiper) onReady(swiper);
  };

  carousel.addEventListener("carousel:ready", (event) => {
    attach(event.detail?.swiper);
  });

  if (carousel.carousel) {
    attach(carousel.carousel);
  }
}

function addPassiveListeners(target, handlers) {
  for (const [event, handler] of Object.entries(handlers)) {
    target.addEventListener(event, handler, { passive: true });
  }
}

/* -------------------------------------------------------------------------- */
/* 2. Parallax                                                                */
/* -------------------------------------------------------------------------- */

class MediaCarouselParallax {
  constructor(section) {
    this.section = section;
    this.shell = section.querySelector(SELECTORS.parallaxShell);
    this.entries = [];
    this.mounted = false;
    this.ticking = false;
    this.swiperAttached = false;
    this.interacting = false;
    this.mountDeferred = false;

    this.onScroll = this.onScroll.bind(this);
    this.update = this.update.bind(this);
    this.onResize = this.onResize.bind(this);
    this.mountAfterInitialScroll = this.mountAfterInitialScroll.bind(this);
  }

  init() {
    if (prefersReducedMotion() || !this.shell) return;

    this.entries = this.collectEntries();
    if (!this.entries.length) return;

    addPassiveListeners(window, {
      scroll: this.onScroll,
      resize: this.onResize,
      load: this.onResize,
      touchmove: this.onScroll,
    });

    if (window.visualViewport) {
      addPassiveListeners(window.visualViewport, {
        resize: this.onResize,
        scroll: this.onScroll,
      });
    }

    bindCarouselReady(this.section, (swiper) => {
      if (this.swiperAttached) return;

      this.swiperAttached = true;
      this.swiper = swiper;

      const syncHorizontal = () => this.syncHorizontalLayout();

      swiper.on("slideChange", syncHorizontal);
      swiper.on("resize", syncHorizontal);
      swiper.on("breakpoint", syncHorizontal);
    });

    this.bootParallax();
  }

  bootParallax() {
    waitForSectionMedia(this.section).then(() => {
      if (this.interacting || this.mounted) return;

      if (window.scrollY <= 1) {
        this.deferMountUntilScroll();
        return;
      }

      this.mountAfterInitialScroll();
    });
  }

  deferMountUntilScroll() {
    if (this.mountDeferred) return;

    this.mountDeferred = true;
    window.addEventListener("scroll", this.mountAfterInitialScroll, {
      passive: true,
    });
  }

  mountAfterInitialScroll() {
    if (this.mountDeferred) {
      this.mountDeferred = false;
      window.removeEventListener("scroll", this.mountAfterInitialScroll);
    }

    if (this.interacting || this.mounted || prefersReducedMotion() || !this.shell) {
      return;
    }

    this.mount();
    this.update();
    requestAnimationFrame(() => {
      this.update();
      if (this.mounted) {
        this.section.classList.add(CLASSES.parallaxReady);
      }
    });
  }

  onSwiperInteractionStart() {
    if (this.interacting) return;

    this.interacting = true;
    this.section.classList.add("media-carousel--swiping");

    if (this.mounted) {
      this.unmount();
    }
  }

  onSwiperInteractionEnd() {
    if (!this.interacting) return;

    this.interacting = false;
    this.section.classList.remove("media-carousel--swiping");

    if (prefersReducedMotion() || !this.shell) return;

    this.mount();
    this.syncHorizontalLayout();
    this.update();
  }

  collectEntries() {
    return Array.from(
      this.section.querySelectorAll(SELECTORS.parallaxTarget),
    ).map((target) => ({
      target,
      media: target.closest(".media-carousel__media"),
      slide: target.closest(".swiper-slide"),
      home: {
        parent: target.parentElement,
        next: target.nextElementSibling,
      },
    }));
  }

  onResize() {
    if (prefersReducedMotion()) {
      this.unmount();
      return;
    }

    if (!this.mounted) {
      this.mount();
    }

    this.syncHorizontalLayout();
    this.update();
  }

  mount() {
    if (this.mounted) return;

    for (const { target } of this.entries) {
      this.shell.appendChild(target);
    }

    this.shell.hidden = false;
    this.mounted = true;
    this.section.classList.add(CLASSES.parallaxMounted);
    this.section.classList.remove(CLASSES.parallaxReady);
    this.syncHorizontalLayout();
  }

  unmount() {
    if (this.mountDeferred) {
      this.mountDeferred = false;
      window.removeEventListener("scroll", this.mountAfterInitialScroll);
    }

    if (!this.mounted) return;

    for (const { target, home } of this.entries) {
      if (home.parent) {
        home.parent.insertBefore(target, home.next);
      }

      this.resetTargetStyles(target);
    }

    this.shell.hidden = true;
    this.mounted = false;
    this.section.classList.remove(CLASSES.parallaxMounted);
    this.section.classList.remove(CLASSES.parallaxReady);
  }

  onScroll() {
    if (!this.mounted || this.ticking) return;

    this.ticking = true;
    requestAnimationFrame(this.update);
  }

  resetTargetStyles(target) {
    target.classList.remove(CLASSES.parallaxActive, CLASSES.parallaxConcealed);
    target.style.removeProperty("left");
    target.style.removeProperty("width");
    target.style.removeProperty("top");
    target.style.removeProperty("height");
    target.style.removeProperty("clip-path");
  }

  clearStyles(target) {
    target.classList.remove(CLASSES.parallaxActive);
    target.classList.add(CLASSES.parallaxConcealed);
    target.style.removeProperty("left");
    target.style.removeProperty("width");
    target.style.removeProperty("top");
    target.style.removeProperty("height");
    target.style.removeProperty("clip-path");
  }

  isSlideVisible(slide) {
    if (!slide || !(slide instanceof HTMLElement)) return false;

    if (slide.classList.contains("swiper-slide-visible")) return true;

    if (!this.swiper?.slides?.length) {
      return slide.getAttribute("aria-hidden") !== "true";
    }

    const slideIndex = parseSlideIndex(slide);
    if (Number.isNaN(slideIndex)) return false;

    return slideIndex === getSwiperIndex(this.swiper);
  }

  syncHorizontalLayout() {
    if (!this.mounted) return;

    for (const { target, media } of this.entries) {
      if (!media) continue;

      const horizontal = getParallaxHorizontalBounds(
        this.section,
        media,
        this.swiper,
      );

      target.style.left = `${horizontal.left}px`;
      target.style.width = `${horizontal.width}px`;
    }
  }

  applyClip(target, mediaRect) {
    const viewportHeight = getViewportMetrics().height;
    const atInitialPageTop = window.scrollY <= 1;

    if (atInitialPageTop) {
      target.classList.remove(CLASSES.parallaxConcealed);
      target.classList.add(CLASSES.parallaxActive);
      target.style.clipPath = "inset(0 0 0 0)";
      return;
    }

    const visibleTop = Math.max(0, mediaRect.top);
    const visibleBottom = Math.min(viewportHeight, mediaRect.bottom);
    const clipTop = Math.max(
      0,
      Math.floor(visibleTop) - PARALLAX_CLIP_OVERSCAN,
    );
    const clipBottom = Math.max(
      0,
      Math.floor(viewportHeight - visibleBottom) - PARALLAX_CLIP_OVERSCAN,
    );

    target.classList.remove(CLASSES.parallaxConcealed);
    target.classList.add(CLASSES.parallaxActive);
    target.style.clipPath = `inset(${clipTop}px 0 ${clipBottom}px 0)`;
  }

  update() {
    this.ticking = false;

    if (!this.mounted || prefersReducedMotion()) return;

    const viewportHeight = getViewportMetrics().height;

    for (const { target, media, slide } of this.entries) {
      if (!media) {
        this.clearStyles(target);
        continue;
      }

      const mediaRect = media.getBoundingClientRect();
      const inView =
        this.isSlideVisible(slide) &&
        mediaRect.bottom > -PARALLAX_CLIP_OVERSCAN &&
        mediaRect.top < viewportHeight + PARALLAX_CLIP_OVERSCAN;

      if (!inView) {
        this.clearStyles(target);
        continue;
      }

      this.applyClip(target, mediaRect);
    }
  }
}

/* -------------------------------------------------------------------------- */
/* 3. Carousel accessibility & pagination                                     */
/* -------------------------------------------------------------------------- */

function updateSlideStatus(section, activeIndex) {
  const liveRegion = section.querySelector(SELECTORS.status);
  if (!liveRegion) return;

  const slideCount = parseInt(section.dataset.slideCount, 10);
  const template = section.dataset.slideStatusTemplate;
  const index = activeIndex + 1;

  liveRegion.textContent = template
    ? template
        .replace("__INDEX__", String(index))
        .replace("__COUNT__", String(slideCount))
    : `Slide ${index} of ${slideCount}`;
}

function pauseAutoplayMedia(section) {
  if (!prefersReducedMotion()) return;

  for (const video of section.querySelectorAll("video[autoplay]")) {
    video.pause();
    video.removeAttribute("autoplay");
  }
}

function warmSlideImages(slide) {
  if (!(slide instanceof HTMLElement)) return;

  for (const source of slide.querySelectorAll("picture source[srcset]")) {
    const src = source.srcset.split(",")[0]?.trim().split(/\s+/)[0];
    if (!src || document.querySelector(`link[rel="preload"][href="${src}"]`)) {
      continue;
    }

    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = src;
    if (source.media) link.media = source.media;
    document.head.appendChild(link);
  }

  for (const img of slide.querySelectorAll("img")) {
    if (img.loading === "lazy") {
      img.loading = "eager";
    }

    if (!img.complete) {
      img.decode?.().catch(() => {});
    }
  }
}

function warmAllCarouselSlides(section) {
  const seen = new Set();

  for (const slide of section.querySelectorAll(".media-carousel__slide")) {
    const index = parseSlideIndex(slide);

    if (!Number.isNaN(index)) {
      if (seen.has(index)) continue;
      seen.add(index);
    }

    warmSlideImages(slide);
  }
}

function waitForSectionMedia(section, timeoutMs = 5000) {
  const images = Array.from(
    section.querySelectorAll(".media-carousel__slide img"),
  );

  if (!images.length) return Promise.resolve();

  const waits = images.map(
    (img) =>
      new Promise((resolve) => {
        if (img.complete && img.naturalWidth > 0) {
          resolve();
          return;
        }

        img.addEventListener("load", resolve, { once: true });
        img.addEventListener("error", resolve, { once: true });
      }),
  );

  return Promise.race([
    Promise.all(waits),
    new Promise((resolve) => {
      window.setTimeout(resolve, timeoutMs);
    }),
  ]);
}

function warmCarouselSlides(section, swiper) {
  warmAllCarouselSlides(section);
}

function isMultiSlideViewport(section, swiper) {
  const slideCount = getSlideCount(section, swiper);
  if (slideCount <= 1) return false;

  const slidesPerView =
    typeof swiper.params.slidesPerView === "number"
      ? swiper.params.slidesPerView
      : parseInt(section.dataset.slidesPerViewDesktop, 10) || 1;

  return slidesPerView >= slideCount;
}

function setSlideVisibility(slide, visible) {
  slide.setAttribute("aria-hidden", visible ? "false" : "true");

  if (visible) {
    slide.removeAttribute("inert");
  } else {
    slide.setAttribute("inert", "");
  }
}

function hasSwiperVisibleClass(swiper) {
  return swiper.slides.some(
    (slide) =>
      slide instanceof HTMLElement &&
      slide.classList.contains("swiper-slide-visible"),
  );
}

function isSlideVisibleForA11y(slide, swiper, index) {
  if (!(slide instanceof HTMLElement)) return false;

  if (hasSwiperVisibleClass(swiper)) {
    return slide.classList.contains("swiper-slide-visible");
  }

  return index === getSwiperIndex(swiper);
}

function updateSlideAccessibility(section, swiper) {
  if (!swiper?.slides) return;

  let slideIndex = 0;
  for (const slide of swiper.slides) {
    setSlideVisibility(slide, isSlideVisibleForA11y(slide, swiper, slideIndex));
    slideIndex++;
  }

  const parallax = section._mediaCarouselParallax;
  if (parallax && !parallax.interacting) {
    parallax.update();
  }

  if (
    isMultiSlideViewport(section, swiper) &&
    section.dataset.paginationActiveIndex !== undefined
  ) {
    updateSlideStatus(
      section,
      parseInt(section.dataset.paginationActiveIndex, 10),
    );
    return;
  }

  updateSlideStatus(section, getSwiperIndex(swiper));
}

function setPaginationBulletState(bullets, activeIndex) {
  for (const bullet of bullets) {
    const bulletIndex = parseSlideIndex(bullet);
    const isActive = bulletIndex === activeIndex;

    bullet.classList.toggle("swiper-pagination-bullet-active", isActive);

    if (isActive) {
      bullet.setAttribute("aria-current", "true");
    } else {
      bullet.removeAttribute("aria-current");
    }
  }
}

function getPaginationNextIndex(key, currentIndex, total) {
  switch (key) {
    case "ArrowRight":
    case "ArrowDown":
      return (currentIndex + 1) % total;
    case "ArrowLeft":
    case "ArrowUp":
      return (currentIndex - 1 + total) % total;
    case "Home":
      return 0;
    case "End":
      return total - 1;
    default:
      return currentIndex;
  }
}

function initPagination(section, swiper) {
  const pagination = section.querySelector(SELECTORS.pagination);
  if (!pagination || !swiper) return;

  const bullets = pagination.querySelectorAll(SELECTORS.paginationBullet);
  if (!bullets.length || pagination.dataset.paginationInit === "true") return;

  pagination.dataset.paginationInit = "true";

  const setActiveSlide = (index) => {
    if (Number.isNaN(index) || index < 0) return;

    section.dataset.paginationActiveIndex = String(index);
    setPaginationBulletState(bullets, index);
    updateSlideStatus(section, index);
  };

  const goToSlide = (index) => {
    setActiveSlide(index);
    swiper.slideTo(index);
  };

  for (const bullet of bullets) {
    bullet.addEventListener("click", () => {
      const index = parseSlideIndex(bullet);
      if (!Number.isNaN(index)) goToSlide(index);
    });
  }

  pagination.addEventListener("keydown", (event) => {
    if (!PAGINATION_KEYS.has(event.key)) return;

    const items = Array.from(bullets);
    const currentIndex = items.findIndex(
      (bullet) => bullet.getAttribute("aria-current") === "true",
    );

    if (currentIndex < 0) return;

    const nextIndex = getPaginationNextIndex(
      event.key,
      currentIndex,
      items.length,
    );

    event.preventDefault();
    goToSlide(nextIndex);
    items[nextIndex].focus();
  });

  const syncFromSwiper = () => {
    if (isMultiSlideViewport(section, swiper)) return;
    setActiveSlide(getSwiperIndex(swiper));
  };

  setActiveSlide(getSwiperIndex(swiper));
  swiper.on("slideChange", syncFromSwiper);
  swiper.on("breakpoint", () => {
    if (!isMultiSlideViewport(section, swiper)) syncFromSwiper();
  });
}

function refreshCarouselLayout(section, swiper) {
  requestAnimationFrame(() => {
    if (swiper.animating) return;

    swiper.update();
    updateSlideAccessibility(section, swiper);
  });
}

function initCarouselResponsive(section, swiper) {
  const desktopMq = window.matchMedia(`(min-width: ${DESKTOP_MIN}px)`);
  const reconcile = () => refreshCarouselLayout(section, swiper);

  swiper.on("breakpoint", reconcile);
  swiper.on("resize", reconcile);
  desktopMq.addEventListener("change", reconcile);
}

function initCarouselAccessibility(section) {
  if (section.dataset.a11yInit === "true") return;

  section.dataset.a11yInit = "true";

  bindCarouselReady(section, (swiper) => {
    const parallax = section._mediaCarouselParallax;
    const syncSlides = () => updateSlideAccessibility(section, swiper);
    const preloadSlides = () => warmCarouselSlides(section, swiper);

    const onInteractionStart = () => {
      parallax?.onSwiperInteractionStart();
    };

    const onInteractionEnd = () => {
      syncSlides();
      parallax?.onSwiperInteractionEnd();
      preloadSlides();
    };

    syncSlides();
    preloadSlides();
    initPagination(section, swiper);
    initCarouselResponsive(section, swiper);

    swiper.on("sliderFirstMove", onInteractionStart);
    swiper.on("slideChangeTransitionStart", () => {
      onInteractionStart();
      syncSlides();
    });
    swiper.on("slideChangeTransitionEnd", onInteractionEnd);
    swiper.on("touchEnd", () => {
      requestAnimationFrame(() => {
        if (!swiper.animating && parallax?.interacting) {
          parallax.onSwiperInteractionEnd();
        }
      });
    });
    swiper.on("slideChange", syncSlides);
    swiper.on("resize", () => parallax?.onResize());
  });
}

const MOBILE_MAX = 1024;
let lockedViewportWidth = window.innerWidth;
let heroHeightListenersBound = false;

function lockMobileHeroHeight(section) {
  if (!window.matchMedia(`(max-width: ${MOBILE_MAX}px)`).matches) {
    section.style.removeProperty("--media-carousel-locked-height");
    delete section.dataset.heroHeightLocked;
    return;
  }

  if (section.dataset.heroHeightLocked) return;

  const height = Math.round(window.innerHeight);
  section.style.setProperty("--media-carousel-locked-height", `${height}px`);
  section.dataset.heroHeightLocked = String(height);
}

function resetMobileHeroHeight(section) {
  delete section.dataset.heroHeightLocked;
  section.style.removeProperty("--media-carousel-locked-height");
  lockMobileHeroHeight(section);
}

function handleHeroHeightResize() {
  const widthChanged = Math.abs(window.innerWidth - lockedViewportWidth) > 32;

  if (!widthChanged) return;

  lockedViewportWidth = window.innerWidth;

  for (const section of document.querySelectorAll(SELECTORS.section)) {
    resetMobileHeroHeight(section);
    section._mediaCarouselParallax?.onResize();
  }
}

function bindHeroHeightListeners() {
  if (heroHeightListenersBound) return;

  heroHeightListenersBound = true;
  window.addEventListener("resize", handleHeroHeightResize, { passive: true });

  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", handleHeroHeightResize);
  }
}

function initParallax(section) {
  if (section.dataset.parallaxInit === "true") return;

  section.dataset.parallaxInit = "true";
  const parallax = new MediaCarouselParallax(section);
  section._mediaCarouselParallax = parallax;
  parallax.init();
}

function initSection(section) {
  lockMobileHeroHeight(section);
  pauseAutoplayMedia(section);
  warmAllCarouselSlides(section);

  if (section.classList.contains(CLASSES.parallax)) {
    initParallax(section);
  }

  initCarouselAccessibility(section);
}

function initAll() {
  bindHeroHeightListeners();

  for (const section of document.querySelectorAll(SELECTORS.section)) {
    initSection(section);
  }
}

function onSectionLoad(event) {
  const section = event.target.querySelector(SELECTORS.section);
  if (!section) return;

  resetMobileHeroHeight(section);
  initSection(section);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initAll);
} else {
  initAll();
}

document.addEventListener("shopify:section:load", onSectionLoad);
